import { useState } from 'react';
import { History, ChevronDown, RotateCcw, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from '@/components/ui/dropdown-menu';
import { formatDistanceToNow } from 'date-fns';
import type { InstrumentRun } from '@/hooks/useInstrumentRuns';

interface RunHistoryDropdownProps {
  runs: InstrumentRun[];
  loading: boolean;
  onLoadRun: (run: InstrumentRun) => void;
  onRerun: (run: InstrumentRun) => void;
  rerunning?: boolean;
}

export function RunHistoryDropdown({ 
  runs, 
  loading, 
  onLoadRun, 
  onRerun,
  rerunning 
}: RunHistoryDropdownProps) {
  const [open, setOpen] = useState(false);

  if (loading) {
    return (
      <Button variant="ghost" size="sm" disabled className="gap-2 text-muted-foreground">
        <History className="h-4 w-4" />
        <Loader2 className="h-3 w-3 animate-spin" />
      </Button>
    );
  }

  if (runs.length === 0) {
    return (
      <Button variant="ghost" size="sm" disabled className="gap-2 text-muted-foreground">
        <History className="h-4 w-4" />
        No history
      </Button>
    );
  }

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" className="gap-2 text-muted-foreground">
          <History className="h-4 w-4" />
          History ({runs.length})
          <ChevronDown className="h-3 w-3" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-72 bg-popover border-border z-50">
        <DropdownMenuLabel className="text-xs text-muted-foreground font-normal">
          Recent runs
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {runs.map((run) => {
          const inputs = run.inputs as Record<string, string>;
          const preview = inputs.ideaDescription || inputs.desiredOutcome || 'Untitled run';
          const truncatedPreview = preview.length > 40 ? preview.slice(0, 40) + '…' : preview;
          
          return (
            <DropdownMenuItem
              key={run.id}
              className="flex flex-col items-start gap-1 py-2 cursor-pointer"
              onClick={() => {
                onLoadRun(run);
                setOpen(false);
              }}
            >
              <div className="flex items-center justify-between w-full gap-2">
                <span className="text-sm font-medium text-foreground truncate flex-1">
                  {truncatedPreview}
                </span>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6 shrink-0"
                  disabled={rerunning}
                  onClick={(e) => {
                    e.stopPropagation();
                    onRerun(run);
                    setOpen(false);
                  }}
                >
                  {rerunning ? (
                    <Loader2 className="h-3 w-3 animate-spin" />
                  ) : (
                    <RotateCcw className="h-3 w-3" />
                  )}
                </Button>
              </div>
              <span className="text-xs text-muted-foreground">
                {formatDistanceToNow(new Date(run.created_at), { addSuffix: true })}
              </span>
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
